import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { List } from "../Utils/List";
import { StringUtils } from "../Utils/StringUtils";
import { ObjUtils } from "../Utils/ObjUtils";
import { HashSet } from "../Utils/HashSet";
import { IpAddress } from "./IpAddress";
import { IpSubnet } from "./IpSubnet";
import { IpRange } from "./IpRange";
import { IpOctetRange } from "./IpOctetRange";

export class IpScope implements ObjectSerializer  {
	private static MAX_BOUNDED_IPSCOPE = 65536;

	private _addresses: List<IpAddress>;
	public get Addresses(): List<IpAddress> {
		return this._addresses;
	}

	private _ranges: List<IpRange>;
	public get Ranges(): List<IpRange> {
		return this._ranges;
	}

	private _subnets: List<IpSubnet>;
	public get Subnets(): List<IpSubnet>{
		return this._subnets;
	}

    private _octetRanges: List<IpOctetRange>;
    public get OctetRanges(): List<IpOctetRange> {
        return this._octetRanges;
    }

    private _hosts: List<string>;
    public get Hosts(): List<string> {
        return this._hosts;
    }

	constructor() {
		this._addresses = new List<IpAddress>();
		this._ranges = new List<IpRange>();
        this._subnets = new List<IpSubnet>();
        this._octetRanges = new List<IpOctetRange>();
		this._hosts = new List<string>();


	}

	public Clear() {
		this._addresses = new List<IpAddress>();
		this._ranges = new List<IpRange>();
		this._subnets = new List<IpSubnet>();
        this._octetRanges = new List<IpOctetRange>();
        this._hosts = new List<string>();
	}

	public Copy(ipScope: IpScope) {
		if (!ipScope)
			return;

		this._addresses = new List<IpAddress>();
		ipScope.Addresses.ForEach((ipAddress) => {
			this.Addresses.Add(ipAddress.Clone());
		});

		this._ranges = new List<IpRange>();
		ipScope.Ranges.ForEach((ipRange) => {
			this.Ranges.Add(ipRange.Clone());
		});

		this._subnets = new List<IpSubnet>();
		ipScope.Subnets.ForEach((ipSubnet) => {
			this.Subnets.Add(ipSubnet.Clone());
        });

        this._octetRanges = new List<IpOctetRange>();
        ipScope.OctetRanges.ForEach((ipOctetRange: IpOctetRange) => {
            this.OctetRanges.Add(ipOctetRange.Clone());
        });

        this._hosts = new List<string>();
        ipScope.Hosts.ForEach((host: string) => {
            this.Hosts.Add(host);
        });
	}

	public get IsEmpty(): boolean {
        return (this.Addresses.Count == 0 && this.Ranges.Count == 0 &&
            this.Subnets.Count == 0 && this.OctetRanges.Count == 0 &&
            this.Hosts.Count == 0);
	}

	public Add(seedAddress: string): boolean {

		// IP address
		// IP subnet
		// IP address range
		// IP address octet range
		// Hostname

		if (StringUtils.IsNullOrWhiteSpace(seedAddress))
			return false;

		let added: boolean = false;

		if (seedAddress.indexOf("/") != -1) {
			added = this.AddSubnetString(seedAddress);
		}

		if (!added && seedAddress.indexOf("-") != -1) {
			added = this.AddRangeString(seedAddress);

			if (!added) {
				added = this.AddOctetRangeString(seedAddress);
			}
		}

		if (!added) {
			added = this.AddAddressString(seedAddress);
		}

		return added;
	}

	public AddAddress(ip: IpAddress): boolean {
		if (ip) {
			this.Addresses.Add(ip);
			return true;
		}
		else {
			return false;
		}
	}

	public AddAddressString(ipStr: string): boolean {
		if (StringUtils.IsNullOrWhiteSpace(ipStr))
			return false;

		let ipAddress = IpAddress.Create(ipStr);
		if (ipAddress) {
			return this.AddAddress(ipAddress);
		}
		else {
			return this.AddHost(ipStr);
		}
	}

	public AddHost(host: string): boolean {
		if (host) {
			this.Hosts.Add(host);
			return true;
		}
		else {
			return false;
		}
	}

	public AddRange(range: IpRange): boolean {
		if (range) {
			this.Ranges.Add(range);
			return true;
		}
		else {
			return false;
		}
	}

	public AddRangeString(rangeStr: string): boolean {
		let ipRange = IpRange.CreateFromString(rangeStr);
		if (ipRange) {
			return this.AddRange(ipRange);
		}
		else {
			return false;
		}
	}

	public AddOctetRange(octetRange: IpOctetRange): boolean {
		if (octetRange) {
			this.OctetRanges.Add(octetRange);
			return true;
		}
		else {
			return false;
		}
	}

	public AddOctetRangeString(octetRangeStr: string): boolean {
		let ipOctetRange = IpOctetRange.Create(octetRangeStr);
		if (ipOctetRange) {
			return this.AddOctetRange(ipOctetRange);
		}
		else {
			return false;
		}
	}

	public AddSubnet(subnet: IpSubnet): boolean {
		if (subnet) {
			this.Subnets.Add(subnet);
			return true;
		}
		else {
			return false;
		}
	}

	public AddSubnetString(subnetStr: string): boolean {
		var ipSubnet = IpSubnet.CreateFromString(subnetStr);
		if (ipSubnet) {
			return this.AddSubnet(ipSubnet);
		}
		else {
			return false;
		}
	}

	public Contains(ipAddr: IpAddress) {
			
		if (this.IpAddresses.Contains(ipAddr))
			return true;

		if (this.Ranges.Some((range) => {
			if (range.Contains(ipAddr))
				return true;
		}))
			return true;

		if (this.Subnets.Some((subnet) => {
			if (subnet.Contains(ipAddr))
				return true;
		}))
            return true;

        if (this.OctetRanges.Some((octetRange) => {
            if (octetRange.Contains(ipAddr))
                return true;
        }))
            return true;

		return false;
	}

	public get IpAddresses(): HashSet<IpAddress> {
		var ipAddresses = new HashSet<IpAddress>();

		// INDIVIDUAL IP ADDRESSES
		ipAddresses.UnionWith(this.Addresses);

		// IP ADDRESSES defined by an IP RANGE
		this.Ranges.ForEach((range) => {
			ipAddresses.UnionWith(range.IpAddresses);
		});

		// IP ADDRESSES defined by an IP SUBNET
		this.Subnets.ForEach((subnet) => {
			ipAddresses.UnionWith(subnet.ValidDiscoveryIpAddresses);
        });

		// IP ADDRESSES defined by an IP OCTET RANGE
        this.OctetRanges.ForEach((octetRange) => {
            ipAddresses.UnionWith(octetRange.IpAddresses);
        });

		return (ipAddresses);
	}

	public get BoundedIpAddresses(): HashSet<IpAddress> { 
		var ipCount = 0;

		var ipAddresses = new HashSet<IpAddress>(this.Addresses);

		// INDIVIDUAL IP ADDRESSES
		ipCount = ipAddresses.Count;

		if (ipCount < IpScope.MAX_BOUNDED_IPSCOPE) {
			var tempIps = null;

			// IP ADDRESSES defined by an IP RANGE
			this.Ranges.Some((ipRange) => {
					
				// CHECK ADDRESS BOUNDS
				if (ipAddresses.Count >= IpScope.MAX_BOUNDED_IPSCOPE)
					return true;

				// GET generated IP list
				tempIps = ipRange.IpAddresses;
				if ((tempIps == null) || (tempIps.Count <= 0))
					return;

				tempIps.Some((ip) => {
					if (ipAddresses.Contains(ip))
						return;

					ipAddresses.Add(ip);

					if (ipAddresses.Count >= IpScope.MAX_BOUNDED_IPSCOPE)
						return true;
				});
			});

			// IP ADDRESSES defined by an IP SUBNET
			this.Subnets.Some((ipSubnet) => {

				// CHECK ADDRESS BOUNDS
				if (ipAddresses.Count >= IpScope.MAX_BOUNDED_IPSCOPE)
					return true;

				// GET generated IP list
				tempIps = ipSubnet.ValidDiscoveryIpAddresses;
				if ((tempIps == null) || (tempIps.Count <= 0))
					return;

				tempIps.Some((ip) => {
					if (ipAddresses.Contains(ip))
						return;

					ipAddresses.Add(ip);

					if (ipAddresses.Count >= IpScope.MAX_BOUNDED_IPSCOPE)
						return true;
				});
			});

            // IP ADDRESSES defined by an IP OCTET RANGE
            this.OctetRanges.Some((octetRange) => {
					
                // CHECK ADDRESS BOUNDS
                if (ipAddresses.Count >= IpScope.MAX_BOUNDED_IPSCOPE)
                    return true;

                // GET generated IP list
                tempIps = octetRange.IpAddresses;
                if ((tempIps == null) || (tempIps.Count <= 0))
                    return;

                tempIps.Some((ip) => {
                    if (ipAddresses.Contains(ip))
                        return;

                    ipAddresses.Add(ip);

                    if (ipAddresses.Count >= IpScope.MAX_BOUNDED_IPSCOPE)
                        return true;
                });
            });
		}

		return (ipAddresses);
	}

	public Equals(obj: any) : boolean {
		if (obj == this)
			return true;
		if (obj == null)
			return false;
		if (!(obj instanceof IpScope))
			return false;

		var other = <IpScope>obj;

		return (ObjUtils.ListEquals(this.Addresses, other.Addresses) &&
			ObjUtils.ListEquals(this.Ranges, other.Ranges) &&
            ObjUtils.ListEquals(this.Subnets, other.Subnets) &&
            ObjUtils.ListEquals(this.OctetRanges, other.OctetRanges) &&
            ObjUtils.ListEquals(this.Hosts, other.Hosts));
	}

	public ToString() : string {
		var ipScope = '';

		if (!this.IsEmpty) {
			if (this.Addresses.Count > 0) {
				this.Addresses.ForEach((ipAddress) => {
					ipScope += ipAddress.toString();
					ipScope += "\r\n";
				});
			}

			if (this.Ranges.Count > 0) {
				this.Ranges.ForEach((ipRange) => {
					ipScope += ipRange.ToString();
					ipScope += "\r\n";
				});
			}

			if (this.Subnets.Count > 0) {
				this.Subnets.ForEach((ipSubnet) => {
					ipScope += ipSubnet.toString();
					ipScope += "\r\n";
				});
			}

            if (this.OctetRanges.Count > 0) {
                this.OctetRanges.ForEach((octetRange) => {
                    ipScope += octetRange.ToString();
                    ipScope += "\r\n";
                });
            }

            if (this.Hosts.Count > 0) {
                this.Hosts.ForEach((hostName) => {
                    ipScope += hostName;
                    ipScope += "\r\n";
                });
            }
		}

		return (ipScope);
    }

	public FromString(str: string): boolean {
		let success = true;

		this.Clear();

		if (str) {
			let lines = str.split('\n');
			lines.forEach((line: string) => {
				if (line) {
					let trimmedLine = line.trim();
					if (trimmedLine) {
						if (!this.Add(trimmedLine)) {
							success = false;
						}
					}
				}
			});
		}

		return success;
	}

	public static readonly ObjSer_ObjectName = "IpScope_ObjectName";

	public Write(obj: any, context: any): void {
		let objName = "ip_scope";
		if (context[IpScope.ObjSer_ObjectName] !== undefined)
			objName = context[IpScope.ObjSer_ObjectName];

		obj[objName] = {};
		obj = obj[objName];

		obj.addresses = [];
		ObjSerUtils.WriteCollection(obj.addresses, this.Addresses, (value: IpAddress) => {
			return { entry: ValueEncoder.EncodeIpAddress(value) };
		}, context);

		obj.ranges = [];
		ObjSerUtils.WriteCollection(obj.ranges, this.Ranges, (value: IpRange) => {
			return { entry: ValueEncoder.EncodeIpRange(value) };
		}, context);

		obj.subnets = [];
		ObjSerUtils.WriteCollection(obj.subnets, this.Subnets, (value: IpSubnet) => {
			return { entry: ValueEncoder.EncodeIpSubnet(value) };
		}, context);

		obj.octet_ranges = [];
		ObjSerUtils.WriteCollection(obj.octet_ranges, this.OctetRanges, (value: IpOctetRange) => {
			return { entry: ValueEncoder.EncodeIpOctetRange(value) };
		}, context);

		obj.hosts = [];
		ObjSerUtils.WriteCollection(obj.hosts, this.Hosts, (value: string) => {
			return { entry: value };
		}, context);
	}

	public Read(obj: any, context: any): void {
		let objName = "ip_scope";
		if (context[IpScope.ObjSer_ObjectName] !== undefined)
			objName = context[IpScope.ObjSer_ObjectName];

		if (obj[objName] === undefined)
			throw new Error();
		obj = obj[objName];

		if (obj.addresses !== undefined) {
			ObjSerUtils.ReadCollection(obj.addresses, this.Addresses, (value: any) => {
				return ValueEncoder.DecodeIpAddress(value.entry);
			}, context);
		}

		if (obj.ranges !== undefined) {
			ObjSerUtils.ReadCollection(obj.ranges, this.Ranges, (value: any) => {
				return ValueEncoder.DecodeIpRange(value.entry);
			}, context);
		}

		if (obj.subnets !== undefined) {
			ObjSerUtils.ReadCollection(obj.subnets, this.Subnets, (value: any) => {
				return ValueEncoder.DecodeIpSubnet(value.entry);
			}, context);
		}

		if (obj.octet_ranges !== undefined) {
			ObjSerUtils.ReadCollection(obj.octet_ranges, this.OctetRanges, (value: any) => {
				return ValueEncoder.DecodeIpOctetRange(value.entry);
			}, context);
		}

		if (obj.hosts !== undefined) {
			ObjSerUtils.ReadCollection(obj.hosts, this.Hosts, (value: any) => {
				return value.entry;
			}, context);
		}
	}

}
	 